/* eslint-disable no-use-before-define */
import _ from 'underscore';
import { useState, useMemo, useCallback, useContext, useEffect, useRef } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/shared-app-capi';
import { createErrorMessage } from '@bingads-webui-campaign/error-utilities';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { initialize } from '@bingads-webui-clientcenter/pidl-telemetry';
import IWindow from '@bingads-webui-clientcenter/window-interface';
import RestfulService from '@bingads-webui-clientcenter/restful-service';
import { useJsDataContext } from '@bingads-webui-cc-react/js-data-rest';
import { useTelemetry } from '../use-telemetry';
import { useLoadingDecorator } from '../use-entity';
import useFeatures from '../use-features';

export default function usePaymentInstrumentEditorProps({
  scope,
  currency,
  hideAddFunds,
  attachSubmitEvent,
  detachSubmitEvent,
  handlePiAddSuccess,
  setLoading,
  setPidlLoading,
}) {
  const pidlLoggerFactory = usePidlLoggerFactory();
  const restfulService = useRestfulService(scope);
  const [{ CCBaseUrl: ccBaseUrl, CurrentLocale: culture }] = useContext(ServerContext);
  const [isPsd2ValidationEnabled] = useFeatures('Psd2Validation').values;

  const ccAPI = useJsDataContext();
  const provinces = useLoadingDecorator(useCallback(countryCode => ccAPI.DomainDataSubGeography.findAll({ countryCode }), [ccAPI]));
  const offlinePaymentInstructions = useLoadingDecorator(useCallback(countryCode => ccAPI.OfflinePaymentInstructions.findAll({ currencyCode: currency, countryCode }), [ccAPI, currency]));
  const { attachPidlSubmit, detachPidlSubmit, setOfflinePiAddress } = usePaymentInstrumentCreation(ccAPI, attachSubmitEvent, detachSubmitEvent, handlePiAddSuccess);

  // TODO: replace the non-sensical IWindow "interface" (ideally, the information should be available from the "scope" parameter)
  const props = useMemo(() => ({
    paymentInstrumentEditorProps: {
      ccBaseUrl,
      restfulService,
      returnUrl: IWindow.location.href,
      isPsd2ValidationEnabled,
      attachSubmitEvent: attachPidlSubmit,
      detachSubmitEvent: detachPidlSubmit,
      handlePiAddSuccess,
      findStates: provinces.factory,
      culture,
      setLoading,
      setPidlLoading,
      pidlLoggerCreate: pidlLoggerFactory,
      isSmartAddFundsEnabled: !hideAddFunds,
    },
    offlinePiProps: {
      fetchInstructions: offlinePaymentInstructions.factory,
      setAddress: setOfflinePiAddress,
    },
  }), [
    ccBaseUrl,
    culture,
    restfulService,
    provinces.factory,
    offlinePaymentInstructions.factory,
    handlePiAddSuccess,
    attachPidlSubmit,
    detachPidlSubmit,
    isPsd2ValidationEnabled,
    setLoading,
    setPidlLoading,
    setOfflinePiAddress,
    pidlLoggerFactory,
    hideAddFunds,
  ]);

  return {
    props,
    isLoading: provinces.isLoading || offlinePaymentInstructions.isLoading,
  };
}

function usePaymentInstrumentCreation(ccAPI, attachSubmitEvent, detachSubmitEvent, handlePiAddSuccess) {
  const [offlinePiAddress, setOfflinePiAddress] = useState();
  const offlinePayment = useLoadingDecorator(useCallback(() => ccAPI.PaymentInstrument.create({ PI: { Type: Constants.PaymentInstrumentType.OfflinePaymentMethod, Address: offlinePiAddress } }), [ccAPI, offlinePiAddress]));

  // NOTE: attach* and handlePi* callbacks allow us to "hook" into the save flow;
  // normally, they allow PIDL editor to be notified when the form is submitted,
  // but if the user selected "Bank Transfer", that is not handled by PIDL,
  // instead we have to manually persist the address the user provided,
  // and use it to create the payment instrument;
  // to keep the illusion of a coherent system, we will reuse the same callbacks
  // and emulate PIDL behavior
  const submitPidl = useRef(_.noop);
  const attachPidlSubmit = useCallback((submit) => {
    submitPidl.current = submit;
  }, []);

  const detachPidlSubmit = useCallback(() => {
    submitPidl.current = _.noop;
  }, []);

  const { error: log } = useTelemetry();
  const createPaymentInstrument = useCallback(() => {
    const isPidl = submitPidl.current !== _.noop;
    if (isPidl) {
      // NOTE: internally, the PIDL flow will call the handlePiAddSuccess callback
      // (unless the flow requires a page reload, e.g. PayPal,
      // in which case we should land back on the page with a PIID in the context)
      submitPidl.current.call();
    } else {
      // TODO: show error notification
      offlinePayment.factory().then(pi => pi.Id).then(handlePiAddSuccess).catch(e => log(`Create Offline PaymentMethod: ${createErrorMessage(e)}`));
    }
  }, [offlinePayment, handlePiAddSuccess, log]);

  useEffect(() => {
    attachSubmitEvent(createPaymentInstrument);
    return () => detachSubmitEvent(createPaymentInstrument);
  }, [attachSubmitEvent, detachSubmitEvent, createPaymentInstrument]);

  return {
    attachPidlSubmit,
    detachPidlSubmit,
    setOfflinePiAddress,
  };
}

export function usePidlLoggerFactory() {
  const { trace: log } = useTelemetry();

  const logPrefix = '[PidlTelemetry][TemplateMigration]';
  const pidlTelemetry = useMemo(() => initialize({
    startScenario: scenarioName => log(`${logPrefix}: Start scenario ${scenarioName}`),
    endScenario: (scenarioName, pass, details) => log(`${logPrefix}: End scenario: ${scenarioName}. Pass: ${pass}. Details: ${details}`),
    trace: (api, message, scenarioName) => log(`${logPrefix}: Api: ${api}. Message: ${message}. ScenarioName: ${scenarioName}`),
  }), [log]);

  const pidlLoggerCreate = useCallback(
    (context, workflow) => pidlTelemetry.create(`${context}`, workflow),
    [pidlTelemetry]
  );

  return pidlLoggerCreate;
}

function useRestfulService(scope) {
  const restfulService = useMemo(
    () => new RestfulService({
      cidFetcher: _.constant(scope.cid),
      uidFetcher: _.constant(scope.uid),
    }),
    [scope.cid, scope.uid]
  );

  return restfulService;
}
